import { useEffect, useRef } from "react";
import { useSession } from "@/contexts/SessionContext";
import { ONESIGNAL_APP_ID } from "./config";

const APP_HOSTNAME = "app.youtemp.co.uk";

declare global {
  interface Window {
    OneSignal?: any;
    OneSignalDeferred?: any[];
    __oneSignalInitStarted?: boolean;
    __oneSignalReady?: boolean;
    __oneSignalExternalId?: string | null;
  }
}

function hostAllowsOneSignal(): boolean {
  if (typeof window === "undefined") return false;
  if (!ONESIGNAL_APP_ID) return false;
  try {
    if (window.self !== window.top) return false;
  } catch {
    return false;
  }
  return window.location.hostname === APP_HOSTNAME;
}

/**
 * Lightweight pub/sub so usePushNotifications can react to readiness changes
 * (login sync complete / logout) without prop drilling.
 */
const readinessListeners = new Set<() => void>();
export function subscribeOneSignalReadiness(cb: () => void): () => void {
  readinessListeners.add(cb);
  return () => { readinessListeners.delete(cb); };
}
export function getOneSignalReady(): boolean {
  return typeof window !== "undefined" && window.__oneSignalReady === true;
}
export function getOneSignalExternalId(): string | null {
  return (typeof window !== "undefined" && window.__oneSignalExternalId) || null;
}
function notifyReadiness() {
  readinessListeners.forEach((cb) => {
    try { cb(); } catch { /* noop */ }
  });
}

function loadSdkOnce() {
  if (window.__oneSignalInitStarted) return;
  window.__oneSignalInitStarted = true;

  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async (OneSignal: any) => {
    try {
      await OneSignal.init({
        appId: ONESIGNAL_APP_ID,
        autoResubscribe: true,
        notifyButton: { enable: false },
        // Belt-and-braces: suppress any automatic prompting / welcome push.
        // Dashboard-side toggles should also be disabled to avoid drift.
        promptOptions: { slidedown: { prompts: [] } },
        welcomeNotification: { disable: true },
        serviceWorkerParam: { scope: "/" },
        serviceWorkerPath: "OneSignalSDKWorker.js",
      });
    } catch (err) {
      console.warn("[OneSignal] init failed", err);
    }
  });

  const s = document.createElement("script");
  s.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
  s.defer = true;
  document.head.appendChild(s);
}

/**
 * Mounts OneSignal only on the authenticated app origin AND only once a
 * Supabase user.id exists. The SDK script is not even fetched on signed-out
 * screens (e.g. /auth), so no prompts, slidedowns, or welcome notifications
 * can fire before login.
 *
 * On sign-out we call OneSignal.logout() to clear the External ID, but we
 * deliberately do NOT call PushSubscription.optOut() — push is a per-user
 * notification preference and must only be revoked from the in-app toggle.
 */
export function OneSignalProvider({ children }: { children: React.ReactNode }) {
  const { user } = useSession();
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hostAllowsOneSignal()) return;
    const desiredId = user?.id ?? null;
    if (desiredId === lastUserIdRef.current) return;
    lastUserIdRef.current = desiredId;

    // Only load the SDK once we actually have a logged-in user.
    if (desiredId) {
      loadSdkOnce();
    } else if (!window.__oneSignalInitStarted) {
      // No SDK ever loaded and no user — nothing to do.
      return;
    }

    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal: any) => {
      try {
        if (desiredId) {
          await OneSignal.login(desiredId);
          window.__oneSignalExternalId = desiredId;
          window.__oneSignalReady = true;
        } else {
          await OneSignal.logout();
          window.__oneSignalExternalId = null;
          window.__oneSignalReady = false;
        }
        notifyReadiness();
      } catch (err) {
        console.warn("[OneSignal] identity sync failed", err);
        window.__oneSignalReady = false;
        notifyReadiness();
      }
    });
  }, [user?.id]);

  return <>{children}</>;
}